import React, { useState, useEffect, useRef } from "react";
import { Moon, Sun, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";

export function Navbar({ darkMode, toggleDarkMode }) {
  const [isVisible, setIsVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const lastY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;

      // small threshold to prevent jitter
      const THRESHOLD = 8;

      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          if (y < 50) {
            // near top: always show
            setIsVisible(true);
          } else if (Math.abs(delta) > THRESHOLD) {
            // scrolling down -> hide, up -> show
            setIsVisible(delta < 0);
          }
          lastY.current = y;
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${
        isVisible ? "translate-y-0" : "-translate-y-full"
      } ${darkMode ? "bg-gray-800 text-white" : "bg-neutral-100 text-gray-900"} shadow-md`}
      role="navigation"
      aria-label="Main"
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Brand */}
          <a href="/" className="text-xl font-bold">
            Rachel Zhu
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-6">
            <a href="/" className="hover:opacity-80 transition-opacity">Home</a>
            <a href="/Experience" className="hover:opacity-80 transition-opacity">Experience</a>
            <a href="/projects" className="hover:opacity-80 transition-opacity">Projects</a>
            <Link to="/contact" className="hover:opacity-80 transition-opacity">Contact</Link>
            <button
              className="p-2 rounded-full bg-secondary dark:bg-secondary-dark hover:opacity-90 transition"
              onClick={toggleDarkMode}
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-md"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu (solid background) */}
        {menuOpen && (
          <div
            id="mobile-menu"
            className={`md:hidden border-t ${
              darkMode
                ? "bg-primary-dark text-white border-white/10"
                : "bg-primary text-gray-900 border-black/10"
            }`}
          >
            <div className="flex flex-col items-center space-y-4 py-4">
              <a href="/" onClick={() => setMenuOpen(false)}>Home</a>
              <a href="/Experience" onClick={() => setMenuOpen(false)}>Experience</a>
              <a href="/projects" onClick={() => setMenuOpen(false)}>Projects</a>
              <Link to="/contact" onClick={() => setMenuOpen(false)}>Contact</Link>
              <button
                className="p-2 rounded-full bg-secondary dark:bg-secondary-dark hover:opacity-90 transition"
                onClick={toggleDarkMode}
                aria-label="Toggle dark mode"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
